import { useEffect, useRef, forwardRef, useImperativeHandle } from "react";

interface PreoWidgetRef {
  reinitialize: () => void;
}

// Track script loading globally to avoid multiple loads
let scriptLoaded = false;
let scriptLoading = false;
let scriptLoadResolve: (() => void) | null = null;

const createScriptLoadPromise = () => {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }

  if ((window as any).customElements?.get("preo-storefront")) {
    scriptLoaded = true;
    return Promise.resolve();
  }

  return new Promise<void>((resolve) => {
    scriptLoadResolve = resolve;
    (window as any).__preoScriptReady = resolve;
  });
};

export default forwardRef<PreoWidgetRef, any>(function PreoWidget(
  {
    scriptUrl = "https://cdn.jsdelivr.net/npm/@preo-live/components@0.6/dist/components.umd.js",
    ...props
  },
  ref,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const elementRef = useRef<HTMLElement | null>(null);
  const isInitializedRef = useRef(false);

  const createElement = async () => {
    if (
      typeof window === "undefined" ||
      !containerRef.current ||
      elementRef.current
    )
      return;

    // Wait for script to be ready
    await createScriptLoadPromise();

    if (!containerRef.current) return; // Component might have unmounted

    const element = document.createElement("preo-storefront");
    Object.entries(props).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (typeof value === 'boolean') {
          if (value === true) {
            // For true booleans, set attribute without value (bare attribute)
            element.setAttribute(key, '');
          }
          // For false booleans, don't set the attribute at all
        } else {
          // For non-boolean values, set as string
          element.setAttribute(key, String(value));
        }
      }
    });

    // Clear container first to avoid duplicates
    containerRef.current.innerHTML = "";
    containerRef.current.appendChild(element);
    elementRef.current = element;
  };

  const removeElement = () => {
    if (elementRef.current && containerRef.current) {
      containerRef.current.removeChild(elementRef.current);
      elementRef.current = null;
    }
  };

  const reinitialize = () => {
    removeElement();
    setTimeout(() => createElement(), 50);
  };

  useImperativeHandle(ref, () => ({
    reinitialize,
  }));

  useEffect(() => {
    if (typeof window === "undefined") return;

    let script: HTMLScriptElement | null = null;

    const loadScript = () => {
      if (scriptLoaded || scriptLoading) {
        createElement();
        return;
      }

      // Check if script already exists
      const existingScript = document.querySelector(
        `script[src="${scriptUrl}"]`,
      );
      if (existingScript) {
        scriptLoading = true;
        existingScript.addEventListener("load", () => {
          scriptLoaded = true;
          if (scriptLoadResolve) {
            scriptLoadResolve();
          }
          createElement();
        });
        return;
      }

      scriptLoading = true;
      script = document.createElement("script");
      script.src = scriptUrl;
      script.async = true;

      script.onload = () => {
        scriptLoaded = true;
        if (scriptLoadResolve) {
          scriptLoadResolve();
        }
        createElement();
      };

      document.head.appendChild(script);
    };

    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      loadScript();
    }

    return () => {
      removeElement();
    };
  }, [scriptUrl]);

  useEffect(() => {
    if (elementRef.current) {
      Object.entries(props).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (typeof value === 'boolean') {
            if (value === true) {
              // For true booleans, set attribute without value (bare attribute)
              elementRef.current!.setAttribute(key, '');
            } else {
              // For false booleans, remove the attribute
              elementRef.current!.removeAttribute(key);
            }
          } else {
            // For non-boolean values, set as string
            elementRef.current!.setAttribute(key, String(value));
          }
        } else {
          elementRef.current!.removeAttribute(key);
        }
      });
    }
  }, [props]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        background: "white",
        alignContent: "center",
        alignItems: "center",
        display: "flex",
        justifyContent: "center",
        padding: "40px",
      }}
    />
  );
});
